import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import AdminNavbar from './AdminNavbar';
import * as FiIcons from 'react-icons/fi';

const { FiHeadphones, FiClock, FiCheck, FiAlertCircle, FiMessageCircle, FiUser, FiCalendar } = FiIcons;

const AdminSupport = () => {
  const [tickets] = useState([
    {
      id: 1234,
      title: 'Monitor not connecting to dashboard',
      user: 'John Doe',
      email: 'john.doe@company.com',
      priority: 'high',
      status: 'open',
      created: '2024-01-15 10:30',
      lastUpdate: '2 hours ago',
      category: 'Technical'
    },
    {
      id: 1235,
      title: 'Billing question about upgrade',
      user: 'Jane Smith',
      email: 'jane.smith@business.com',
      priority: 'medium',
      status: 'in-progress',
      created: '2024-01-15 09:15',
      lastUpdate: '1 hour ago',
      category: 'Billing'
    },
    {
      id: 1236,
      title: 'Feature request: Custom templates',
      user: 'Mike Johnson',
      email: 'mike.j@startup.io',
      priority: 'low',
      status: 'resolved',
      created: '2024-01-14 16:45',
      lastUpdate: '1 day ago',
      category: 'Feature Request'
    },
    {
      id: 1237,
      title: 'Unable to upload video files',
      user: 'Sarah Wilson',
      email: 'sarah.w@corp.com',
      priority: 'high',
      status: 'open',
      created: '2024-01-15 14:20',
      lastUpdate: '30 minutes ago',
      category: 'Technical'
    }
  ]);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredTickets = tickets.filter(ticket => 
    filterStatus === 'all' || ticket.status === filterStatus
  );

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-400 bg-red-500/20';
      case 'medium':
        return 'text-yellow-400 bg-yellow-500/20';
      case 'low':
        return 'text-green-400 bg-green-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-red-400 bg-red-500/20';
      case 'in-progress':
        return 'text-yellow-400 bg-yellow-500/20';
      case 'resolved':
        return 'text-green-400 bg-green-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return FiAlertCircle;
      case 'in-progress':
        return FiClock;
      case 'resolved':
        return FiCheck;
      default:
        return FiMessageCircle;
    }
  };

  return (
    <div className="min-h-screen">
      <AdminNavbar />
      
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Support Center</h1>
              <p className="text-white/70">
                Manage customer support tickets and inquiries
              </p>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Open Tickets</p>
                  <p className="text-2xl font-bold text-white">
                    {tickets.filter(t => t.status === 'open').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiAlertCircle} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">In Progress</p>
                  <p className="text-2xl font-bold text-white">
                    {tickets.filter(t => t.status === 'in-progress').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiClock} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Resolved</p>
                  <p className="text-2xl font-bold text-white">
                    {tickets.filter(t => t.status === 'resolved').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Tickets</p>
                  <p className="text-2xl font-bold text-white">{tickets.length}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiHeadphones} className="text-white text-xl" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center space-x-4">
              <p className="text-white/80 font-medium">Filter by status:</p>
              <div className="flex space-x-2">
                {['all', 'open', 'in-progress', 'resolved'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      filterStatus === status
                        ? 'bg-red-500/20 text-red-400 border border-red-400/30'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Tickets List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/20">
              <h2 className="text-xl font-semibold text-white">Support Tickets ({filteredTickets.length})</h2>
            </div>
            
            <div className="divide-y divide-white/10">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="p-6 hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${
                        ticket.priority === 'high' ? 'from-red-400 to-pink-400' :
                        ticket.priority === 'medium' ? 'from-yellow-400 to-orange-400' :
                        'from-green-400 to-emerald-400'
                      } rounded-xl flex items-center justify-center`}>
                        <SafeIcon icon={getStatusIcon(ticket.status)} className="text-white text-xl" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-semibold">#{ticket.id} - {ticket.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace('-', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-white/70">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUser} className="text-xs" />
                            <span>{ticket.user}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} className="text-xs" />
                            <span>{ticket.created}</span>
                          </div>
                          <span>Category: {ticket.category}</span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right text-sm">
                      <p className="text-white/70">Last update</p>
                      <p className="text-white">{ticket.lastUpdate}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Ticket #{selectedTicket.id}
                </h2>
                <p className="text-white/70">{selectedTicket.title}</p>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiUser} className="text-white/70" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-white/70 text-sm mb-1">Customer</p>
                  <p className="text-white">{selectedTicket.user}</p>
                  <p className="text-white/60 text-sm">{selectedTicket.email}</p>
                </div>
                <div>
                  <p className="text-white/70 text-sm mb-1">Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status.replace('-', ' ')}
                  </span>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/70 text-sm mb-2">Customer Message:</p>
                <p className="text-white">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
                </p>
              </div>

              <div className="flex space-x-4">
                <button className="flex-1 px-4 py-3 bg-green-500/20 border border-green-400/30 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors">
                  Mark as Resolved
                </button>
                <button className="flex-1 px-4 py-3 bg-yellow-500/20 border border-yellow-400/30 text-yellow-400 rounded-xl hover:bg-yellow-500/30 transition-colors">
                  In Progress
                </button>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminSupport;